import AppSaveBar from '@/components/AppSaveBar/AppSaveBar';
import Layout from '@/components/layout';
import { BlockStack, Button } from '@shopify/polaris';

import SkeletonPage from '@/components/SkeletonPage';
import { config } from '@/config';
import { dateFormatEnum, PricingPlan, timeFormatEnum } from '@/constants/enum';
import { isAvailableFeature, isEmpty, openErrorToast, openToast } from '@/helpers';
import { useGetAppearanceSettingQuery, useGetGeneralSettingQuery, useUpdateGeneralSettingMutation } from '@/redux/query';
import { handleSaveOrderPaginationSettings, orderTableSettingSelector } from '@/redux/slice/order.slice';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ConnectGoogle from './ConnectGoogle';
import OrderTable from './OrderTable';
import { ActionModal } from './OrderTable/component/Modal/ActionModal';
import { DraftOrderModal } from './OrderTable/component/Modal/DraftOrderModal';
import { ExportOrderModal } from './OrderTable/component/Modal/ExportOrderModal';
import useTitleBar from '@/hooks/useTitleBar';

const OrderReport = () => {
  const { t } = useTranslation('orderReport');
  const {
    data: generalSettingData,
    isLoading: isLoadingGetGeneralSetting,
    refetch: refetchGetGeneralSetting,
  } = useGetGeneralSettingQuery({ shop: config.shop });
  const [updateGeneralSetting, { isLoading: isLoadingUpdateGeneralSetting }] = useUpdateGeneralSettingMutation();
  const orderTableSettings = useSelector(orderTableSettingSelector);
  const { data: widgetSettingData, isLoading: isLoadingWidgetSetting } = useGetAppearanceSettingQuery({ shop: config.shop });

  console.log(widgetSettingData, 'data');

  const dispatch = useDispatch();
  const [calendar, setCalendar] = useState({
    time: '',
    timeBackup: '',
    disable: false,
  });
  const [modal, setModal] = useState({
    isOpenDraft: false,
    isOpenExport: false,
    isOpenStatus: false,
  });

  useEffect(() => {
    setCalendar({
      time: generalSettingData?.data.googleCalendarTimeSlot || '',
      timeBackup: generalSettingData?.data.googleCalendarTimeSlot || '',
      disable: !isAvailableFeature(PricingPlan.Smart, generalSettingData?.data.plan as PricingPlan),
    });
  }, [generalSettingData?.data.googleCalendarTimeSlot, generalSettingData?.data.plan]);

  const handleUpdateCalendarTimeSlot = () => {
    updateGeneralSetting({
      shop: config.shop,
      googleCalendarTimeSlot: calendar.time,
    })
      .unwrap()
      .then(() => {
        openToast(dispatch);
        refetchGetGeneralSetting();
      })
      .catch(() => openErrorToast(dispatch));
  };

  const primaryAction = useMemo(
    () => (
      <Button variant="primary" onClick={() => setModal((prev) => ({ ...prev, isOpenDraft: true }))}>
        {t('reportTable.primaryAction')}
      </Button>
    ),
    [t],
  );
  const secondaryAction = useMemo(
    () => [
      {
        content: t('reportTable.statusControl'),
        onAction: () => setModal((prev) => ({ ...prev, isOpenStatus: true })),
      },
      {
        content: t('reportTable.exportCSV'),
        onAction: () => setModal((prev) => ({ ...prev, isOpenExport: true })),
        disabled: isEmpty(orderTableSettings),
      },
    ],
    [orderTableSettings, t],
  );

  useEffect(() => {
    dispatch(
      handleSaveOrderPaginationSettings({
        page: 1,
      }),
    );
  }, [dispatch]);

  useTitleBar({ title: t('page.title') });

  return (
    <Layout primaryAction={primaryAction} secondaryActions={secondaryAction}>
      <AppSaveBar
        open={calendar.time !== calendar.timeBackup}
        onSave={handleUpdateCalendarTimeSlot}
        onDiscard={() => setCalendar((prev) => ({ ...prev, time: calendar.timeBackup }))}
        loading={isLoadingUpdateGeneralSetting}
      />
      {isLoadingGetGeneralSetting || isLoadingWidgetSetting ? (
        <SkeletonPage />
      ) : (
        <BlockStack gap="400">
          <OrderTable />
          <ConnectGoogle calendar={calendar} setTime={setCalendar} />
          <DraftOrderModal
            open={modal.isOpenDraft}
            onCancel={() => setModal((prev) => ({ ...prev, isOpenDraft: false }))}
            dateFormat={widgetSettingData?.data.customizations.dateFormat || dateFormatEnum.DAY_MONTH_YEAR}
            timeFormat={widgetSettingData?.data.customizations.timeFormat || timeFormatEnum.TWELVE_HOURS_FORMAT}
          />
          <ExportOrderModal open={modal.isOpenExport} onCancel={() => setModal((prev) => ({ ...prev, isOpenExport: false }))} />
          <ActionModal
            open={modal.isOpenStatus}
            onCancel={() => setModal((prev) => ({ ...prev, isOpenStatus: false }))}
            isActiveAction={!!generalSettingData?.data.isAutoFullfillmentAction}
          />
        </BlockStack>
      )}
    </Layout>
  );
};

export default OrderReport;
